import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';

/**
 * Modal pour la validation par la directrice marketing
 */
const DirectriceValidationModal = ({ requestId, requestType, onClose, onValidated }) => {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [request, setRequest] = useState(null);
  const [supplySource, setSupplySource] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Récupérer les informations sur la demande et la source d'approvisionnement
  useEffect(() => {
    const fetchRequestData = async () => {
      try {
        // Récupérer les détails de la demande
        const requestResponse = await api.get(`/api/requests/${requestId}`);
        setRequest(requestResponse.data.data.request);
        
        // Récupérer la source d'approvisionnement
        const supplySourceResponse = await api.marketingValidation.getRequestSupplySource(requestId);
        if (supplySourceResponse.data && supplySourceResponse.data.data) {
          setSupplySource(supplySourceResponse.data.data);
        }
        
        setIsLoadingData(false);
      } catch (error) {
        console.error('Error fetching request data:', error);
        setError('Erreur lors de la récupération des données de la demande');
        setIsLoadingData(false);
      }
    };
    
    fetchRequestData();
  }, [requestId]);
  
  // Déterminer le message à afficher en fonction du type de demande et de la source d'approvisionnement
  const getNextStepMessage = () => {
    if (request) {
      if (request.requestType === 'business_unit') {
        return 'Après validation, une note interne sera générée avec votre signature. La demande sera ensuite marquée comme terminée.';
      } else if (supplySource && supplySource.category === 'internal') {
        return 'Après validation, une note interne sera générée avec votre signature. La demande sera ensuite marquée comme terminée.';
      } else if (request.requestType === 'animation_activation') {
        return 'Après validation, une note interne sera générée avec votre signature. La demande sera ensuite transmise au service commercial pour la dernière étape de validation.';
      } else {
        return 'Après validation, une note interne sera générée avec votre signature. La demande sera ensuite transmise au service commercial pour la dernière étape de validation.';
      }
    }
    return 'Après validation, une note interne sera générée avec votre signature.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    
    try {
      // Appel API pour valider la demande
      const response = await api.directrice.validate(requestId, { comment });
      
      // Message de succès adapté en fonction du type de demande et de la source d'approvisionnement
      let successMessage = 'Demande validée avec succès. Note interne générée';
      
      if (request) {
        if (request.requestType === 'business_unit' || (supplySource && supplySource.category === 'internal')) {
          successMessage += '. La demande a été complétée.';
        } else {
          successMessage += ' et transmise au service commercial.';
        }
      }
      
      toast.success(successMessage);
      
      // Notifier le composant parent
      if (onValidated) {
        onValidated(response.data.data);
      }
      
      // Fermer la modal
      onClose();
    } catch (error) {
      console.error('Error validating request:', error);
      setError(error.response?.data?.error || 'Erreur lors de la validation');
      toast.error(error.response?.data?.error || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Validation Directrice Marketing"
      size="md"
      footer={
        <>
          <button
            type="button"
            className="modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            form="directrice-validation-form"
            className="modal-btn-success"
            disabled={loading}
          >
            {loading ? 'Validation...' : 'Valider et générer la note interne'}
          </button>
        </>
      }
    >
      <form id="directrice-validation-form" onSubmit={handleSubmit}>
        <p className="text-sm text-gray-500 mb-4">
          En validant cette demande, vous allez générer une note interne qui servira de document officiel pour autoriser la sortie des produits.
        </p>

        {error && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700">
            Commentaire (optionnel)
          </label>
          <textarea
            id="comment"
            name="comment"
            rows="3"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md"
            placeholder="Ajouter un commentaire..."
            disabled={loading}
          ></textarea>
        </div>

        {/* Information sur le workflow */}
        <div className="bg-blue-50 p-3 rounded-md mb-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
            </div>
            <div className="ml-3 flex-1 md:flex md:justify-between">
              <p className="text-sm text-blue-700">
                <strong>Note :</strong> {isLoadingData ? 'Chargement...' : getNextStepMessage()}
              </p>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default DirectriceValidationModal;
